import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakReminder } from '../contexts/BreakReminderContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTime } from '../contexts/TimeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { playPresetSound, DEFAULT_SOUND_ID } from '../utils/soundPresets';
import { decideNotificationPolicy } from '../notifications/NotificationPolicy';
import { getAppState } from '../utils/appState';
import { getLockActive } from '../utils/lockActiveStore';
import i18n from '../i18n';

const useBreakReminderAlarms = () => {
  const { t } = useTranslation('shared');
  const { breaks } = useBreakReminder();
  const { addNotification } = useNotification();
  const { timeHHmm, dayKey, dateKey } = useTime();
  const { preferences } = useSettings();
  const { isPlusUser, user } = useAuth();
  const notifiedRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const lastCheckedMinuteRef = useRef(null);

  if (!notifiedRef.current) {
    notifiedRef.current = new Map();
  }

  const normalizeTime = (timeStr) => {
    if (!timeStr) return null;
    if (typeof timeStr === 'string') {
      const cleaned = timeStr.trim();
      const match = cleaned.match(/^\s*(\d{1,2})\D?(\d{2})/);
      if (match) {
        const hour = match[1]?.padStart(2, '0');
        const minute = match[2]?.padStart(2, '0');
        return `${hour}:${minute}`;
      }
    }
    return null;
  };

  // timeHHmm zaten dakika formatında, direkt kullanıyoruz
  const currentClockMinute = timeHHmm;

  useEffect(() => {
    if (!currentClockMinute || !dayKey || !dateKey) {
      return;
    }

    // İlk yükleme - sadece dakikayı kaydet, tetikleme
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastCheckedMinuteRef.current = currentClockMinute;
      return;
    }

    // Dakika değişmediyse, hiçbir şey yapma
    if (lastCheckedMinuteRef.current === currentClockMinute) {
      return;
    }

    // Dakika değişti, güncelle
    lastCheckedMinuteRef.current = currentClockMinute;

    if (!notifiedRef.current) {
      notifiedRef.current = new Map();
    }

    const nowTimestamp = Date.now();

    breaks.forEach((breakItem) => {
      if (!breakItem?.activeDays) {
        return;
      }

      const isActiveToday =
        breakItem.activeDays[dayKey] ??
        breakItem.activeDays[dayKey?.toUpperCase?.()] ??
        breakItem.activeDays[
          dayKey ? `${dayKey.charAt(0).toUpperCase()}${dayKey.slice(1)}` : undefined
        ];

      if (!isActiveToday) {
        return;
      }

      const reminderTime = normalizeTime(breakItem.startTime);

      if (reminderTime !== currentClockMinute) {
        return;
      }

      const notificationKey = `${breakItem.id}-${dateKey}-${currentClockMinute}`;
      const lastNotificationTime = notifiedRef.current.get(notificationKey);

      if (lastNotificationTime && nowTimestamp - lastNotificationTime <= 60000) {
        return;
      }

      const showSystemNotification = preferences.showBreakNotifications !== false;
      const playSoundPreference = preferences.playNotificationSound !== false;
      const soundEnabledForBreak = breakItem.soundEnabled !== false;
      const shouldPlaySound = playSoundPreference && soundEnabledForBreak;

      // Get notification policy
      const appState = getAppState();
      const policy = decideNotificationPolicy({
        type: 'break',
        appState,
      });

      // Apply policy decisions (sanitize when app lock active)
      const locked = getLockActive(user?.id);
      const notifTitle = locked
        ? i18n.t('lock:notifications.breakTitle')
        : t('breakAlarm.breakStartedTitle');
      const notifBody = locked
        ? i18n.t('lock:notifications.breakBody')
        : t('breakAlarm.breakStartedBody', { name: breakItem.name });

      if (showSystemNotification && policy.showSystem) {
        if (window.api?.showNotification) {
          window.api
            .showNotification({
              title: notifTitle,
              body: notifBody,
              sound: shouldPlaySound && policy.playSound,
              meta: {
                type: 'break',
                breakId: breakItem.id,
                breakName: locked ? undefined : breakItem.name,
              },
            })
            .catch((error) => {
              console.error('OS bildirimi gösterilirken hata:', error);
            });
        } else if ('Notification' in window) {
          try {
            if (Notification.permission === 'granted') {
              new Notification(notifTitle, {
                body: notifBody,
                icon: '/favicon.ico',
              });
            } else if (Notification.permission !== 'denied') {
              Notification.requestPermission().then((permission) => {
                if (permission === 'granted') {
                  new Notification(notifTitle, {
                    body: notifBody,
                    icon: '/favicon.ico',
                  });
                }
              });
            }
          } catch (error) {
            console.error('HTML5 bildirimi gösterilirken hata:', error);
          }
        }
      }

      if (policy.showToast && !locked) {
        // Show toast notification
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'break',
              title: t('breakAlarm.breakTimeTitle'),
              message: t('breakAlarm.breakTimeMessage', { name: breakItem.name, minutes: breakItem.durationMinutes }),
            },
          }));
        }
      }

      if (shouldPlaySound && policy.playSound) {
        try {
          // Plus üyeler için varsayılan olarak Plus ses preset'i kullan
          let soundId =
            preferences.breakNotificationSoundId ||
            preferences.notificationSoundId;
          if (!soundId) {
            // Plus üye ise varsayılan Plus sesi, değilse standart ses
            soundId = isPlusUser ? 'plus-luxury' : DEFAULT_SOUND_ID;
          }
          playPresetSound(soundId);
        } catch (error) {
          console.error('Bildirim sesi çalınırken hata:', error);
        }
      }

      notifiedRef.current.set(notificationKey, nowTimestamp);

      if (!locked) {
        addNotification({
          type: 'break',
          title: t('breakAlarm.breakTimeTitle'),
          message: t('breakAlarm.breakTimeMessage', { name: breakItem.name, minutes: breakItem.durationMinutes }),
          data: { breakItem },
        });
      }

      console.log(`✅ Mola hatırlatıcısı tetiklendi: ${breakItem.name} - ${currentClockMinute}`);
    });

    if (notifiedRef.current) {
      const oneDayAgo = nowTimestamp - 24 * 60 * 60 * 1000;
      const keysToDelete = [];
      notifiedRef.current.forEach((timestamp, key) => {
        if (timestamp < oneDayAgo) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => {
        notifiedRef.current.delete(key);
      });
    }
  }, [
    breaks,
    addNotification,
    timeHHmm, // Sadece timeHHmm kullanıyoruz (dakika bazında güncellenir)
    dayKey,
    dateKey,
    preferences.showBreakNotifications,
    preferences.playNotificationSound,
    preferences.notificationSoundId,
    preferences.breakNotificationSoundId,
  ]);
};

export default useBreakReminderAlarms;

